<script setup>
// SiteUp: Central de ajuda. Uma linha por tela coberta pelo onboarding, em
// grupos por mundo do Hub, com "Ler o manual" (abre o manual daquela tela
// sem sair daqui) e "Refazer o tour" (vai para a tela e replay do tour).
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';
import { useAccount } from 'dashboard/composables/useAccount';
import { usePolicy } from 'dashboard/composables/usePolicy';
import { useOnboardingSurface } from 'dashboard/composables/useOnboardingSurface';
import { openPageManual } from 'dashboard/composables/usePageCoach';
import { requestTourReplay } from 'dashboard/composables/useGuidedTour';
import {
  PAGE_COACH,
  screenIconFor,
  worldLabelKeyFor,
} from 'dashboard/helper/onboardingRegistry';
import { TOURS } from 'dashboard/helper/onboardingTours';
import { HUB_WORLDS } from 'dashboard/routes/dashboard/hub/hubWorlds';
import BrasaMascot from 'dashboard/components-next/onboarding/BrasaMascot.vue';

const { t } = useI18n();
const router = useRouter();
const { accountScopedRoute } = useAccount();
const { enabled } = useOnboardingSurface();
// Mesma guarda das Novidades: tela que a pessoa não pode abrir (permissão,
// recurso desligado na conta, tipo de instalação) não entra na lista. Antes
// apareciam Configurações, Campanhas e Kanban para quem não tinha acesso, e
// "Refazer o tour" terminava num redirect seco para o Hub.
const { shouldShow } = usePolicy();

const targetFor = coach => {
  const name = coach.routeName || coach.key;
  return router.getRoutes().find(item => item.name === name) || null;
};

// Sem rota registrada (manual de um painel, por exemplo) a linha fica: o
// manual abre aqui mesmo e não depende de acesso à tela.
const canOpen = coach => {
  const target = targetFor(coach);
  if (!target) return true;
  const meta = target.meta || {};
  return shouldShow(
    meta.featureFlag || '',
    meta.permissions ?? [],
    meta.installationTypes ?? []
  );
};

// Telas que precisam de parametro (uma conversa aberta, por exemplo) nao tem
// "ir para a tela" generico: oferecem so o manual.
const routeFor = coach => {
  const name = coach.routeName || coach.key;
  const target = targetFor(coach);
  if (!target) return null;
  const needsParams = /:(?!accountId)/.test(target.path);
  if (needsParams) return null;
  return accountScopedRoute(name, {}, coach.view ? { view: coach.view } : {});
};

const groups = computed(() => {
  const order = ['hub', ...HUB_WORLDS.map(world => world.id)];
  const byWorld = new Map();
  Object.values(PAGE_COACH).forEach(coach => {
    if (!canOpen(coach)) return;
    const world = coach.key === 'focus_hub' ? 'hub' : coach.world || 'outros';
    if (!byWorld.has(world)) byWorld.set(world, []);
    byWorld.get(world).push({
      key: coach.key,
      titleKey: coach.titleKey,
      bodyKey: coach.bodyKey,
      icon: screenIconFor(coach.routeName || coach.key),
      hasManual: Boolean(coach.manualKey),
      hasTour: Boolean(TOURS[coach.key]),
      to: routeFor(coach),
    });
  });
  return order
    .filter(id => byWorld.has(id))
    .concat([...byWorld.keys()].filter(id => !order.includes(id)))
    .map(id => ({
      id,
      labelKey:
        id === 'hub'
          ? 'HUB.TITLE'
          : HUB_WORLDS.find(world => world.id === id)?.labelKey ||
            worldLabelKeyFor(byWorld.get(id)[0].key),
      items: byWorld.get(id),
    }));
});

const readManual = item => openPageManual(item.key);

const replayTour = async item => {
  if (!item.to) return;
  await router.push(item.to);
  requestTourReplay(item.key);
};
</script>

<template>
  <div
    class="flex h-full w-full flex-col overflow-y-auto bg-n-background"
    data-testid="page-onboarding-help"
  >
    <div class="mx-auto flex w-full max-w-4xl flex-col gap-6 px-6 py-8">
      <header class="flex items-start gap-4">
        <BrasaMascot :size="56" state="idle" :look="90" class="shrink-0" />
        <div class="flex min-w-0 flex-col gap-1">
          <h1 class="m-0 text-2xl font-semibold text-n-slate-12">
            {{ t('ONBOARDING.HELP.TITLE') }}
          </h1>
          <p class="m-0 max-w-2xl text-sm leading-relaxed text-n-slate-11">
            {{ t('ONBOARDING.HELP.BODY') }}
          </p>
        </div>
      </header>

      <p
        v-if="!enabled"
        class="m-0 rounded-xl border border-n-weak bg-n-solid-2 p-4 text-sm text-n-slate-11"
        data-testid="onboarding-help-desktop-only"
      >
        {{ t('ONBOARDING.HELP.DESKTOP_ONLY') }}
      </p>

      <section
        v-for="group in groups"
        :key="group.id"
        class="flex flex-col gap-2"
        :data-testid="`onboarding-help-world-${group.id}`"
      >
        <h2
          class="m-0 text-[11px] font-semibold uppercase tracking-[0.14em] text-n-slate-10"
        >
          {{ t(group.labelKey) }}
        </h2>
        <ul class="m-0 flex list-none flex-col gap-2 p-0">
          <li
            v-for="item in group.items"
            :key="item.key"
            class="flex flex-wrap items-center gap-3 rounded-2xl border border-n-weak bg-n-solid-2 px-4 py-3"
            :data-testid="`onboarding-help-item-${item.key}`"
          >
            <span
              class="flex size-9 shrink-0 items-center justify-center rounded-full bg-n-alpha-2 text-n-slate-11"
              aria-hidden="true"
            >
              <span class="size-4" :class="item.icon" />
            </span>
            <div class="flex min-w-0 flex-1 flex-col">
              <span class="text-sm font-semibold text-n-slate-12">
                <!-- eslint-disable-next-line @intlify/vue-i18n/no-dynamic-keys -->
                {{ t(item.titleKey) }}
              </span>
              <span class="text-xs leading-relaxed text-n-slate-11">
                <!-- eslint-disable-next-line @intlify/vue-i18n/no-dynamic-keys -->
                {{ t(item.bodyKey) }}
              </span>
            </div>
            <div class="flex shrink-0 items-center gap-2">
              <button
                v-if="item.hasManual"
                type="button"
                class="inline-flex min-h-9 items-center gap-1.5 rounded-full bg-n-slate-12 px-3 text-xs font-semibold text-n-background hover:opacity-90 disabled:opacity-40"
                :disabled="!enabled"
                :data-testid="`onboarding-help-manual-${item.key}`"
                @click="readManual(item)"
              >
                <span class="i-ri-book-open-line size-3.5" aria-hidden="true" />
                {{ t('ONBOARDING.HELP.READ_MANUAL') }}
              </button>
              <button
                v-if="item.hasTour && item.to"
                type="button"
                class="inline-flex min-h-9 items-center gap-1.5 rounded-full px-3 text-xs font-medium text-n-slate-11 hover:bg-n-alpha-2 disabled:opacity-40"
                :disabled="!enabled"
                :data-testid="`onboarding-help-tour-${item.key}`"
                @click="replayTour(item)"
              >
                <span class="i-ri-refresh-line size-3.5" aria-hidden="true" />
                {{ t('ONBOARDING.HELP.REPLAY_TOUR') }}
              </button>
            </div>
          </li>
        </ul>
      </section>
    </div>
  </div>
</template>
